// src/services/authService.js
import TokenService from './tokenService';
import { parseJwt } from '../utils/jwtUtils';
import { ROLES } from '../api/constants';
import { normalizePermission } from '../utils/permissions';
import { tenantApi } from './tenantApi';

// Store reference will be injected
let store = null;

class AuthService {
  // Initialize with Redux store
  static setStore(storeInstance) {
    store = storeInstance;
  }

  static async login(email) {
    try {
      if (!store) {
        throw new Error('Store not initialized in AuthService');
      }

      // Use RTK Query mutation via store dispatch
      const result = await store.dispatch(
        tenantApi.endpoints.loginGetCode.initiate(email)
      ).unwrap();
      
      // Save email in token service
      TokenService.setStoredEmail(email);
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Unified Authentication with JWT Claims
   * Simplified approach using JWT as the primary source of truth
   */
  static async verifyCode(email, code) {
    try {
      if (!store) {
        throw new Error('Store not initialized in AuthService');
      }

      const tokenResponse = await store.dispatch(
        tenantApi.endpoints.verifyAndGetToken.initiate({ email, code })
      ).unwrap();

      const { token, refreshToken, refreshTokenExpiryTime } = tokenResponse || {};
      const claims = token ? parseJwt(token) : null;

      if (!claims && token) {
        throw new Error('Invalid token format');
      }

      const roleClaimRaw = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const roleClaims = Array.isArray(roleClaimRaw) ? roleClaimRaw : [roleClaimRaw];

      let userRole = ROLES.USER;
      if (roleClaims.includes('Administrator')) {
        userRole = ROLES.ADMIN;
      } else if (roleClaims.includes('TenantAdmin')) {
        userRole = ROLES.TENANT_ADMIN;
      }

      const userId = claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
      const tenantId = claims.TenantId;

      const rawPermissions = claims.Permissions || [];
      const normalizedPermissions = rawPermissions.map(normalizePermission);

      const baseUserData = {
        email: claims.Email,
        role: userRole,
        userId,
        tenantId,
        permissions: normalizedPermissions,
        rawPermissions,
        lastTokenRefresh: Date.now()
      };

      if (userRole === ROLES.ADMIN) {
        const userData = {
          ...baseUserData,
          validationResult: {
            hasAccess: true,
            reason: 'admin_full_access',
            permissions: normalizedPermissions,
            tenantModules: [],
            sources: { jwt: normalizedPermissions, api: [], tenant: [] }
          }
        };
        TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: userData }, { isLogin: true });
        return { verified: true, token, refreshToken, user: userData, authMethod: 'jwt_claims_admin' };
      }

      const tenantModules = claims.Modules || [];

      if (userRole === ROLES.TENANT_ADMIN) {
        const userData = {
          ...baseUserData,
          tenantModules,
          validationResult: {
            hasAccess: true,
            reason: 'tenant_admin_access',
            permissions: normalizedPermissions,
            tenantModules,
            sources: { jwt: normalizedPermissions, api: [], tenant: tenantModules }
          }
        };
        TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: userData }, { isLogin: true });
        return { verified: true, token, refreshToken, user: userData, authMethod: 'jwt_claims_tenant_admin' };
      }

      if (userRole === ROLES.USER) {
        const hasPermissions = normalizedPermissions.length > 0;
        const userData = {
          ...baseUserData,
          tenantModules,
          validationResult: hasPermissions
            ? {
                hasAccess: true,
                reason: 'permissions_granted',
                permissions: normalizedPermissions,
                tenantModules,
                sources: { jwt: normalizedPermissions, api: [], tenant: tenantModules }
              }
            : {
                hasAccess: false,
                reason: 'no_permissions',
                permissions: [],
                tenantModules
              }
        };
        TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: userData }, { isLogin: true });
        return {
          verified: true,
          token,
          refreshToken,
          user: userData,
          ...(hasPermissions ? {} : { accessDenied: true, reason: 'no_permissions' }),
          authMethod: 'jwt_tenant_modules'
        };
      }

      throw new Error('Invalid user role');

    } catch (error) {
      console.error('Verification error:', error);
      throw error;
    }
  }



  static async refreshToken() {
    try {
      const currentRefreshToken = TokenService.getRefreshToken();
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }
      
      if (!store) {
        throw new Error('Store not initialized in AuthService');
      }

      // Use RTK Query mutation via store dispatch
      const tokenResponse = await store.dispatch(
        tenantApi.endpoints.refreshAuthToken.initiate(currentRefreshToken)
      ).unwrap();
      
      const { token, refreshToken, refreshTokenExpiryTime } = tokenResponse;
      const claims = parseJwt(token);
      if (!claims) throw new Error('Invalid token format');
      
      // Handle role array from JWT
      const roleClaimRaw = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const roleClaims = Array.isArray(roleClaimRaw) ? roleClaimRaw : [roleClaimRaw];
      
      let userRole = ROLES.USER; // default
      if (roleClaims.includes('Administrator')) {
        userRole = ROLES.ADMIN;
      } else if (roleClaims.includes('TenantAdmin')) {
        userRole = ROLES.TENANT_ADMIN;
      } else if (roleClaims.includes('User')) {
        userRole = ROLES.USER;
      }
      
      // Extract tenant modules from JWT
      const tenantModules = claims.Modules || [];
      
      // Normalize permissions
      const rawPermissions = claims.Permissions || [];
      const normalizedPermissions = rawPermissions.map(permission => {
        return normalizePermission(permission);
      });
      
      const userData = {
        email: claims.Email,
        role: userRole,
        userId: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
        tenantId: claims.TenantId,
        permissions: normalizedPermissions,
        rawPermissions: rawPermissions,
        tenantModules: tenantModules, // Add tenant modules from JWT
        lastTokenRefresh: Date.now()
      };
      
      TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: userData }, { isLogin: false });
      return { token, refreshToken, user: userData };
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  static logout() {
    // Broadcast logout to other tabs - this is the ONLY place that should broadcast
    TokenService.clearTokens({ broadcast: true });
    TokenService.setStoredEmail(null);
    return true;
  }

  static isAuthenticated() {
    return TokenService.isAuthenticated();
  }

  static getAuthHeader() {
    const token = TokenService.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  static getCurrentUser() {
    return TokenService.getUser();
  }
  
  // Check if the current session is valid and user is authenticated
  static validateSession() {
    const token = TokenService.getAccessToken();
    if (!token) return false;
    
    // Check if token is expired
    if (TokenService.isTokenExpired(token)) {
      // Try to use refresh token if available
      const refreshToken = TokenService.getRefreshToken();
      if (!refreshToken) {
        this.logout();
        return false;
      }
      
      // Token refresh will be handled by an interceptor
      // Here we just return false to indicate the current session is invalid
      return false;
    }
    
    return true;
  }
}

export default AuthService;